import { computed } from 'vue';
import type { Ref } from 'vue';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  getRelatiesGetOverzichtCountQueryKey,
  getRelatiesGetOverzichtQueryKey,
  useRelatiesGetOverzicht,
  useRelatiesGetOverzichtCount,
} from '@services/crm-public/orval-api/relaties-api/relaties-api';
import type { RelatiesGetOverzichtParams } from '@services/crm-public/orval-api/model/relatiesGetOverzichtParams';
import type { RelatiesGetOverzichtCountParams } from '@services/crm-public/orval-api/model/relatiesGetOverzichtCountParams';
import apiService from '@services/base/apiService';
import type { Relatie, RelatieResponse, SearchParams } from './types';

export const relatieStatus = {
  None: 0,
  Prospect: 1,
  Beeindigd: 2,
  Actief: 3,
} as const;

export interface RelatiesOverzichtOptions {
  naamNummer: Ref<string>;
  limit: Ref<number>;
  offset: Ref<number>;
  isMuterenEnRoyeren: Ref<boolean>;
  searchParams: Ref<SearchParams>;
  adviseurId: Ref<number | null | undefined>;
}

function voegQueryParameterToe(
  query: string,
  key: string,
  value: number | string | undefined,
): string {
  if (value) {
    return `${query}&${key}=${value}`;
  }
  return query;
}

/**
 * Voor het overzicht gebruiken we een nieuw endpoint (betere performance). Voor uitgebreid zoeken nog een oud (.NET Framework) endpoint.
 * Als het nieuwe endpoint alle uitgebreid zoeken functies ook heef ingebouwd, kan de aanroep daarvan van de oude weg.
 */
export function useRelatiesOverzicht(options: RelatiesOverzichtOptions) {
  const { naamNummer, limit, offset, isMuterenEnRoyeren, searchParams, adviseurId } = options;
  const queryClient = useQueryClient();

  const gebruikOudEndpoint = computed(() =>
    Object.values(searchParams.value).some((v) => !!v && v.length > 0),
  );

  const uitgebreidUrl = computed(() => {
    const status = isMuterenEnRoyeren.value ? relatieStatus.Actief : relatieStatus.None;
    let endpoint = `/net/relation/search?aantal=${limit.value}&offset=${offset.value}&naamOfNummer=${naamNummer.value}&status=${status}`;
    endpoint = voegQueryParameterToe(endpoint, 'adviseurId', adviseurId.value ?? undefined);
    endpoint = voegQueryParameterToe(endpoint, 'postcode', searchParams.value.postcode);
    endpoint = voegQueryParameterToe(endpoint, 'kvknummer', searchParams.value.kvknummer);
    endpoint = voegQueryParameterToe(endpoint, 'email', searchParams.value.email);
    endpoint = voegQueryParameterToe(endpoint, 'contactpersoon', searchParams.value.contactpersoon);
    endpoint = voegQueryParameterToe(endpoint, 'polisnummer', searchParams.value.polisnummer);
    endpoint = voegQueryParameterToe(endpoint, 'kenteken', searchParams.value.kenteken);
    endpoint = voegQueryParameterToe(endpoint, 'factuurnummer', searchParams.value.factuurnummer);
    endpoint = voegQueryParameterToe(endpoint, 'schadenummer', searchParams.value.schadenummer);
    endpoint = voegQueryParameterToe(endpoint, 'iban', searchParams.value.iban);
    return endpoint;
  });

  const uitgebreidQueryKey = computed(() => [
    'net',
    'relation',
    'search',
    offset.value,
    naamNummer.value,
    adviseurId.value,
    searchParams.value.postcode,
    searchParams.value.kvknummer,
    searchParams.value.email,
    searchParams.value.contactpersoon,
    searchParams.value.polisnummer,
    searchParams.value.kenteken,
    searchParams.value.factuurnummer,
    searchParams.value.schadenummer,
    searchParams.value.iban,
  ]);

  const {
    data: uitgebreidData,
    isLoading: uitgebreidIsLoading,
    isFetching: uitgebreidIsFetching,
    isError: uitgebreidIsError,
    error: uitgebreidError,
  } = useQuery({
    queryKey: uitgebreidQueryKey,
    queryFn: () => apiService.apiClient.get<RelatieResponse>(uitgebreidUrl.value),
    select: (r) => r.data,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    enabled: gebruikOudEndpoint,
  });

  const overzichtParams = computed<RelatiesGetOverzichtParams>(() => ({
    naamNummer: naamNummer.value,
    adviseurnummer: adviseurId.value ?? undefined,
    limit: limit.value,
    enkelActieve: isMuterenEnRoyeren.value,
    offset: offset.value,
  }));

  const countParams = computed<RelatiesGetOverzichtCountParams>(() => ({
    naamNummer: naamNummer.value,
    enkelActieve: isMuterenEnRoyeren.value,
    adviseurnummer: adviseurId.value ?? undefined,
  }));

  const {
    data: overzichtData,
    isPending: overzichtIsPending,
    isFetching: overzichtIsFetching,
    isError: overzichtIsError,
    error: overzichtError,
  } = useRelatiesGetOverzicht(overzichtParams, {
    query: {
      select: (r) => r.result,
      enabled: computed(() => !gebruikOudEndpoint.value),
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
    },
  });

  const { data: countData } = useRelatiesGetOverzichtCount(countParams, {
    query: {
      select: (r) => r.result,
      enabled: computed(() => !gebruikOudEndpoint.value),
      staleTime: 60 * 1000,
    },
  });

  const mappedRelaties = computed<Relatie[]>(() =>
    (overzichtData.value ?? []).map((item) => ({
      RelatieNummer: item.relatienummer,
      RelatieNaam: item.achternaam,
      TypeRelatie: item.soort as 'P' | 'Z',
      Adviseurnaam: item.naamAdviseur,
      Adviseurnummer: item.adviseurnummer,
      StatusNaam: item.status,
      Adres: item.straat,
      Plaats: item.woonplaats,
    })),
  );

  const relaties = computed<Relatie[]>(() =>
    gebruikOudEndpoint.value ? (uitgebreidData.value?.items ?? []) : mappedRelaties.value,
  );

  const totalItems = computed<number>(() =>
    gebruikOudEndpoint.value
      ? (uitgebreidData.value?.totalItems ?? 0)
      : Number(countData.value ?? 0),
  );

  const isFetching = computed<boolean>(() =>
    gebruikOudEndpoint.value ? uitgebreidIsFetching.value : overzichtIsFetching.value,
  );

  const isLoading = computed<boolean>(() =>
    gebruikOudEndpoint.value ? uitgebreidIsLoading.value : overzichtIsPending.value,
  );

  const isError = computed<boolean>(() =>
    gebruikOudEndpoint.value ? uitgebreidIsError.value : overzichtIsError.value,
  );

  const error = computed(() =>
    gebruikOudEndpoint.value ? uitgebreidError.value : overzichtError.value,
  );

  function invalideerCache() {
    if (gebruikOudEndpoint.value) {
      queryClient.invalidateQueries({ queryKey: uitgebreidQueryKey.value });
    } else {
      queryClient.invalidateQueries({ queryKey: getRelatiesGetOverzichtQueryKey(overzichtParams) });
      queryClient.invalidateQueries({
        queryKey: getRelatiesGetOverzichtCountQueryKey(countParams),
      });
    }
  }

  return {
    relaties,
    totalItems,
    isFetching,
    isLoading,
    isError,
    error,
    isUitgebreidZoeken: gebruikOudEndpoint,
    invalideerCache,
  };
}
